import { alpha, createTheme } from '@mui/material';
import type {
  PaletteMode,
  PaletteOptions,
  Theme,
  ThemeOptions
} from '@mui/material';

type CustomVariant =
  | 'displayXL'
  | 'displayL'
  | 'displayM'
  | 'displayS'
  | 'titleXL'
  | 'titleL'
  | 'titleM'
  | 'titleS'
  | 'bodyXL'
  | 'bodyL'
  | 'bodyM'
  | 'bodyS';

declare module '@mui/material/styles' {
  interface TypographyVariants extends Record<
    CustomVariant,
    React.CSSProperties
  > {}
  interface TypographyVariantsOptions extends Partial<
    Record<CustomVariant, React.CSSProperties>
  > {}
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides extends Record<
    CustomVariant,
    true
  > {}
}

const fontStack =
  '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

const baseOptions: ThemeOptions = {
  typography: {
    fontFamily: fontStack,

    // Display variants
    displayXL: {
      fontFamily: fontStack,
      fontSize: '120px',
      fontWeight: 400,
      letterSpacing: '-0.04em',
      lineHeight: 1.15
    },
    displayL: {
      fontFamily: fontStack,
      fontSize: '96px',
      fontWeight: 500,
      letterSpacing: '-0.02em',
      lineHeight: 1.15
    },
    displayM: {
      fontFamily: fontStack,
      fontSize: '64px',
      fontWeight: 500,
      letterSpacing: '-0.02em',
      lineHeight: 1.15
    },
    displayS: {
      fontFamily: fontStack,
      fontSize: '56px',
      fontWeight: 500,
      letterSpacing: '-0.02em',
      lineHeight: 1.15
    },

    // Title variants
    titleXL: {
      fontFamily: fontStack,
      fontSize: '40px',
      fontWeight: 500,
      letterSpacing: '-0.02em',
      lineHeight: 1.15
    },
    titleL: {
      fontFamily: fontStack,
      fontSize: '32px',
      fontWeight: 500,
      letterSpacing: '-0.02em',
      lineHeight: 1.15
    },
    titleM: {
      fontFamily: fontStack,
      fontSize: '24px',
      fontWeight: 500,
      letterSpacing: '-0.02em',
      lineHeight: 1.15
    },
    titleS: {
      fontFamily: fontStack,
      fontSize: '16px',
      fontWeight: 500,
      letterSpacing: '-0.02em',
      lineHeight: 1.15
    },

    // Body variants
    bodyXL: {
      fontFamily: fontStack,
      fontSize: '32px',
      fontWeight: 400,
      letterSpacing: '0em',
      lineHeight: 1.45
    },
    bodyL: {
      fontFamily: fontStack,
      fontSize: '24px',
      fontWeight: 400,
      letterSpacing: '0em',
      lineHeight: 1.45
    },
    bodyM: {
      fontFamily: fontStack,
      fontSize: '16px',
      fontWeight: 400,
      letterSpacing: '0em',
      lineHeight: 1.45
    },
    bodyS: {
      fontFamily: fontStack,
      fontSize: '14px',
      fontWeight: 400,
      letterSpacing: '0em',
      lineHeight: 1.45
    },

    // Standard MUI variants
    h1: {
      fontFamily: fontStack,
      fontSize: '64px',
      fontWeight: 500,
      letterSpacing: '-0.02em',
      lineHeight: 1.15
    },
    h2: {
      fontFamily: fontStack,
      fontSize: '56px',
      fontWeight: 500,
      letterSpacing: '-0.02em',
      lineHeight: 1.15
    },
    h3: {
      fontFamily: fontStack,
      fontSize: '40px',
      fontWeight: 500,
      letterSpacing: '-0.02em',
      lineHeight: 1.15
    },
    h4: {
      fontFamily: fontStack,
      fontSize: '32px',
      fontWeight: 500,
      letterSpacing: '-0.02em',
      lineHeight: 1.15
    },
    h5: {
      fontFamily: fontStack,
      fontSize: '24px',
      fontWeight: 500,
      letterSpacing: '-0.02em',
      lineHeight: 1.15
    },
    h6: {
      fontFamily: fontStack,
      fontSize: '16px',
      fontWeight: 500,
      letterSpacing: '-0.02em',
      lineHeight: 1.15
    },
    body1: {
      fontFamily: fontStack,
      fontSize: '16px',
      fontWeight: 400,
      letterSpacing: '0em',
      lineHeight: 1.45
    },
    body2: {
      fontFamily: fontStack,
      fontSize: '14px',
      fontWeight: 400,
      letterSpacing: '0em',
      lineHeight: 1.45
    },
    button: {
      fontFamily: fontStack,
      fontSize: '14px',
      fontWeight: 500,
      letterSpacing: '0em',
      textTransform: 'none'
    },
    caption: {
      fontFamily: fontStack,
      fontSize: '12px',
      fontWeight: 400,
      letterSpacing: '0em',
      lineHeight: 1.45
    },
    overline: {
      fontFamily: fontStack,
      fontSize: '12px',
      fontWeight: 500,
      letterSpacing: '0.05em',
      lineHeight: 1.5,
      textTransform: 'uppercase'
    }
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontFamily: fontStack
        }
      }
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          h1: 'h1',
          h2: 'h2',
          h3: 'h3',
          h4: 'h4',
          h5: 'h5',
          h6: 'h6',
          subtitle1: 'h6',
          subtitle2: 'h6',
          body1: 'p',
          body2: 'p'
        }
      }
    }
  }
};

// Brand colors are mode-independent.
const brandPalette: PaletteOptions = {
  primary: {
    main: '#20406C',
    light: '#80D3FF',
    dark: '#162B47',
    contrastText: '#fff'
  },
  secondary: {
    main: '#8F4D18',
    light: '#FFBA6A',
    dark: '#5F3310',
    contrastText: '#fff'
  },
  info: {
    main: '#733786',
    light: '#E9BBF8',
    dark: '#4D2459',
    contrastText: '#fff'
  },
  success: {
    main: '#336A50',
    light: '#C4F6DF',
    dark: '#224736',
    contrastText: '#fff'
  }
};

// Surface/text values are mapped from the legacy styled-components theme
// (src/theme.ts) so MUI-rendered pages match the styled-components pages
// in both modes.
const palettes: Record<PaletteMode, PaletteOptions> = {
  light: {
    ...brandPalette,
    mode: 'light',
    background: {
      default: '#F3F6FB',
      paper: '#FFFFFF'
    },
    text: {
      primary: '#393939',
      secondary: '#42526E',
      disabled: '#C4C4C4'
    },
    divider: '#C4C4C4',
    action: {
      hover: 'rgba(0, 0, 0, 0.05)'
    }
  },
  dark: {
    ...brandPalette,
    mode: 'dark',
    background: {
      default: '#11141E',
      paper: '#1D202D'
    },
    text: {
      primary: '#FFFFFF',
      secondary: 'rgba(255, 255, 255, 0.7)',
      disabled: 'rgba(255, 255, 255, 0.4)'
    },
    divider: 'rgba(255, 255, 255, 0.16)',
    action: {
      hover: 'rgba(255, 255, 255, 0.1)'
    }
  }
};

// Cached per mode: a stable theme reference keeps MuiThemeProvider's context
// value identical across re-renders (layout.js calls this inline on every
// render) and across route remounts, avoiding subtree-wide re-renders.
const themeCache: Partial<Record<PaletteMode, Theme>> = {};

const buildMuiTheme = (mode: PaletteMode): Theme => {
  const theme = createTheme({ ...baseOptions, palette: palettes[mode] });
  if (mode === 'light') return theme;
  // Brand navy (primary.main) is illegible on dark surfaces, so primary
  // links, text/outlined buttons, and outlined chips swap to the light
  // brand blue in dark mode.
  return createTheme(theme, {
    components: {
      MuiLink: {
        styleOverrides: {
          root: ({ ownerState }: { ownerState: { color?: unknown } }) => ({
            ...(ownerState.color === 'primary' && {
              color: theme.palette.primary.light
            })
          })
        }
      },
      MuiButton: {
        styleOverrides: {
          outlinedPrimary: {
            color: theme.palette.primary.light,
            borderColor: alpha(theme.palette.primary.light, 0.5),
            '&:hover': {
              borderColor: theme.palette.primary.light,
              backgroundColor: alpha(theme.palette.primary.light, 0.08)
            }
          },
          textPrimary: {
            color: theme.palette.primary.light,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.light, 0.08)
            }
          }
        }
      },
      MuiChip: {
        styleOverrides: {
          root: ({
            ownerState
          }: {
            ownerState: { color?: unknown; variant?: unknown };
          }) => ({
            ...(ownerState.color === 'primary' &&
              ownerState.variant === 'outlined' && {
                color: theme.palette.primary.light,
                borderColor: alpha(theme.palette.primary.light, 0.5)
              })
          })
        }
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: theme.palette.primary.light
          }
        }
      },
      MuiTab: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              color: theme.palette.primary.light
            }
          }
        }
      }
    }
  });
};

export const getMuiTheme = (mode: PaletteMode): Theme =>
  (themeCache[mode] ??= buildMuiTheme(mode));

// Static light theme kept for existing imports; mode-aware code should use
// getMuiTheme(mode) via AppThemeProvider instead.
export const muiTheme = getMuiTheme('light');
